import { useMemo } from "react";
import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";

interface RichTextRendererProps {
  /** Raw stored notes string. Treated as HTML if it contains tags,
   *  otherwise rendered as preserved-newline plain text — keeps the
   *  legacy textarea-era notes readable until they're re-saved through
   *  the rich editor. */
  content: string | null | undefined;
  className?: string;
}

const HTML_TAG_RE = /<\/?[a-zA-Z][^>]*>/;

export function RichTextRenderer({ content, className }: RichTextRendererProps) {
  const safeHtml = useMemo(() => {
    if (!content) return null;
    if (!HTML_TAG_RE.test(content)) {
      // Plain text — escape and preserve newlines as <br>. We still
      // route through DOMPurify defensively in case the legacy field
      // contains HTML-like fragments.
      const escaped = content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
      return DOMPurify.sanitize(escaped);
    }
    return DOMPurify.sanitize(content, {
      // Tiptap's StarterKit produces only block + inline tags; we
      // explicitly allow the few we render and drop the rest. Anchor
      // attributes match what the editor sets on Link.
      ALLOWED_TAGS: [
        "p",
        "strong",
        "em",
        "u",
        "s",
        "ul",
        "ol",
        "li",
        "blockquote",
        "br",
        "a",
        "code",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "class"],
    });
  }, [content]);

  if (!safeHtml) return null;
  return (
    <div
      className={cn(
        "prose prose-sm max-w-none text-slate-700",
        "prose-p:my-2 prose-ul:my-2 prose-ol:my-2",
        "prose-blockquote:my-2 prose-blockquote:border-l-2 prose-blockquote:border-slate-300 prose-blockquote:pl-3 prose-blockquote:italic prose-blockquote:text-slate-600",
        "prose-a:text-indigo-600 prose-a:underline prose-a:underline-offset-2",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}
